import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Stack,
  Pagination,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";

import useGetAdvancedSurveys from "../../hooks/useGetAdvancedSurveys";
import useDeleteAdvancedSurveyMutation from "../../hooks/useDeleteAdvancedSurveyMutation";
import NavBar from "../../components/navBar/navBar";
import { formatDate, showApiErrorToast } from "../../utils/helpers";
import { USER_ROLES } from "../../utils/constants";
import Loader from "../../components/Loader";
import CustomButton from "../../components/UI/CustomButton";

const headerCellSx = {
  backgroundColor: "primary.50",
  color: "text.primary",
  fontWeight: 600,
  whiteSpace: "nowrap",
  "&.MuiTableCell-stickyHeader": {
    backgroundColor: "primary.50",
  },
};

const AdvancedSurveys = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const userData = JSON.parse(localStorage.getItem("user_data") || "null");
  const showCreator = userData?.role === USER_ROLES.SUPER_ADMIN;

  // Delete dialog state
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);

  const { data, isLoading, isError, error } = useGetAdvancedSurveys({
    page,
    pageSize: 10,
    search,
  });

  const { mutate: deleteSurvey, isLoading: isDeleting } = useDeleteAdvancedSurveyMutation();

  const surveys = data?.results || [];
  const totalPages = Math.ceil((data?.count || 0) / 10);
  const columns = [
    { key: "name", label: "Name" },
    { key: "questions", label: "Questions" },
    { key: "updated", label: "Updated" },
    ...(showCreator ? [{ key: "creator", label: "Created by" }] : []),
    { key: "actions", label: "Actions", align: "right" },
  ];

  useEffect(() => {
    if (isError) {
      showApiErrorToast(error, "Failed to load surveys");
    }
  }, [isError, error]);

  const openDeleteConfirm = (survey) => {
    setSelectedSurvey(survey);
    setOpenDeleteDialog(true);
  };

  const closeDeleteConfirm = () => {
    setOpenDeleteDialog(false);
    setSelectedSurvey(null);
  };

  const handleConfirmDelete = () => {
    if (!selectedSurvey) return;

    deleteSurvey(
      { surveyId: selectedSurvey.id },
      {
        onError: (err) => {
          showApiErrorToast(err, "Failed to delete ePRO");
        },
        onSuccess: () => {
          closeDeleteConfirm();
        },
      }
    );
  };

  return (
    <Box sx={{ maxWidth: "95%", mx: "auto" }}>
      <NavBar active="advanced_surveys" />

      <Box py={2}>
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          mb={2}
          width={"100%"}
        >
          <TextField
            size="small"
            placeholder="Search by ePRO name"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            sx={{
              maxWidth: 300,
              "& .MuiOutlinedInput-root": {
                borderRadius: 999,
                "& fieldset": { borderColor: "divider" },
              },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              },
              htmlInput: { "data-testid": "epro-search-input" },
            }}
          />
          <CustomButton
            size="small"
            data-testid="epro-add-btn"
            onClick={() => navigate("/advanced-surveys-creator")}
          >
            Add ePRO
          </CustomButton>
        </Stack>

        {isLoading ? (
          <Loader />
        ) : (
          <TableContainer
            component={Paper}
            sx={{ boxShadow: 1, border: "0.5px solid", borderColor: "grey.200" }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.key} align={column.align} sx={headerCellSx}>
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {surveys.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      <Typography
                        data-testid="epro-empty-state"
                        color="text.secondary"
                        sx={{ py: 4 }}
                      >
                        No ePRO found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  surveys.map((survey) => (
                    <TableRow
                      key={survey.id}
                      hover
                      data-testid="epro-row"
                      data-epro-name={survey.name}
                    >
                      <TableCell>{survey.name}</TableCell>
                      <TableCell>{survey.question_count ?? 0}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatDate(survey.updated_at, "MM/DD/YYYY, h:mm A")}
                      </TableCell>
                      {showCreator && <TableCell>{survey.created_by_email}</TableCell>}
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        <IconButton
                          size="small"
                          data-testid="epro-edit-btn"
                          onClick={() =>
                            navigate("/advanced-surveys-creator", {
                              state: { surveyId: survey.id },
                            })
                          }
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          data-testid="epro-delete-btn"
                          onClick={() => openDeleteConfirm(survey)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Stack alignItems="center" mt={3}>
            <Pagination count={totalPages} page={page} onChange={(_, value) => setPage(value)} />
          </Stack>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={closeDeleteConfirm} maxWidth="xs" fullWidth>
        <DialogTitle>Delete ePRO</DialogTitle>

        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedSurvey?.name}</strong>?
          </Typography>
          <Typography variant="caption">This action cannot be undone.</Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeDeleteConfirm} disabled={isDeleting}>
            Cancel
          </Button>

          <Button
            data-testid="confirm-delete-epro-btn"
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdvancedSurveys;
